/**
 * The running AI-spend readout: a header chip with the estimated cost so far, opening a
 * per-call-type breakdown.
 *
 * The numbers are the ones already recorded on the run (`run_data.ai_cost_total`, written by
 * `log_openai_cost` on every OpenAI call and persisted with the run — see
 * `clean_run_data_for_save`), so this component only formats what the engine banked; it never
 * re-derives a cost of its own. Input tokens are shown split into CACHED (prompt-cache hits,
 * billed at the discounted cached rate) and fresh, because a long chat is mostly cache hits and
 * the raw input count on its own reads far more expensive than the person is actually paying.
 */

import { useState } from "react";
import { useProfile } from "../state/ProfileContext";
import { Modal } from "../ui/common";
import { titleCase, tokens, usd } from "../ui/format";

/** Friendly names for the openai.json call types (unknown keys fall back to title case). */
const SCHEMA_LABELS: Record<string, string> = {
  rectification_fact_extraction: "Event extraction",
  trait_chat: "Trait chat",
  time_compare_chat: "Compare chat",
  photo_traits: "Photo read",
};

function label_for(schema: string): string {
  return SCHEMA_LABELS[schema] ?? titleCase(schema);
}

interface CostTotals {
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  input_cost: number;
  cached_input_cost: number;
  output_cost: number;
  total_cost: number;
  calls: number;
  by_schema: Record<string, Record<string, any>>;
  updated_at: string | null;
}

const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function read_totals(raw: any): CostTotals {
  const src = raw && typeof raw === "object" ? raw : {};
  const by_schema = src["by_schema"] && typeof src["by_schema"] === "object" ? src["by_schema"] : {};
  return {
    input_tokens: num(src["input_tokens"]),
    cached_input_tokens: num(src["cached_input_tokens"]),
    output_tokens: num(src["output_tokens"]),
    total_tokens: num(src["total_tokens"]),
    input_cost: num(src["input_cost"]),
    cached_input_cost: num(src["cached_input_cost"]),
    output_cost: num(src["output_cost"]),
    total_cost: num(src["total_cost"]),
    calls: num(src["calls"]),
    by_schema,
    updated_at: typeof src["updated_at"] === "string" ? src["updated_at"] : null,
  };
}

export function AiCostBadge() {
  const { run_data } = useProfile();
  const [open, setOpen] = useState(false);
  const t = read_totals(run_data?.ai_cost_total);

  // Nothing has been spent yet (a fresh profile, or a session with no key) — no chip to show.
  if (t.calls === 0) return null;

  // Token counts are always real; the cost is only as real as the per-1M rates in openai.json.
  // All-zero rates would otherwise read as "the AI is free", so say so instead.
  const rates_missing = t.total_tokens > 0 && t.total_cost === 0;
  const rows = Object.entries(t.by_schema).sort((a, b) => num(b[1]?.total_cost) - num(a[1]?.total_cost));

  return (
    <>
      <button
        className="ai-cost-chip"
        onClick={() => setOpen(true)}
        title="Estimated OpenAI spend on this person so far — click for the breakdown"
      >
        <span className="ai-cost-chip-k">AI spend</span>
        <span className="ai-cost-chip-v">{rates_missing ? "rates not set" : usd(t.total_cost)}</span>
        <span className="ai-cost-chip-sub">
          {t.calls} call{t.calls === 1 ? "" : "s"}
        </span>
      </button>

      {open && (
        <Modal title="AI usage & estimated cost" onClose={() => setOpen(false)}>
          <div className="ai-cost-head">
            <div className="ai-cost-total">{rates_missing ? "—" : usd(t.total_cost)}</div>
            <div className="caption">
              estimated spend across {t.calls} AI call{t.calls === 1 ? "" : "s"} for this person
              {t.updated_at ? ` · last call ${t.updated_at.replace("T", " ").replace("Z", " UTC")}` : ""}
            </div>
          </div>

          {rates_missing && (
            <div className="banner warn">
              Token counts below are exact, but every per-1M-token rate in <code>openai.json</code> is
              still <code>0</code>, so the cost totals read as zero. Fill in{" "}
              <code>models.&lt;model&gt;.input_cost_per_1m</code>,{" "}
              <code>cached_input_cost_per_1m</code> and <code>output_cost_per_1m</code> to price them.
            </div>
          )}

          <table className="ai-cost-table">
            <tbody>
              <tr>
                <th>Input tokens</th>
                <td className="mono">{tokens(t.input_tokens)}</td>
                <td className="mono">{rates_missing ? "—" : usd(t.input_cost)}</td>
              </tr>
              <tr className="sub">
                <th>↳ cached (discounted)</th>
                <td className="mono">{tokens(t.cached_input_tokens)}</td>
                <td className="mono">{rates_missing ? "—" : usd(t.cached_input_cost)}</td>
              </tr>
              <tr className="sub">
                <th>↳ fresh</th>
                <td className="mono">{tokens(Math.max(0, t.input_tokens - t.cached_input_tokens))}</td>
                <td className="mono">{rates_missing ? "—" : usd(t.input_cost - t.cached_input_cost)}</td>
              </tr>
              <tr>
                <th>Output tokens</th>
                <td className="mono">{tokens(t.output_tokens)}</td>
                <td className="mono">{rates_missing ? "—" : usd(t.output_cost)}</td>
              </tr>
              <tr className="grand">
                <th>Total</th>
                <td className="mono">{tokens(t.total_tokens)}</td>
                <td className="mono">{rates_missing ? "—" : usd(t.total_cost)}</td>
              </tr>
            </tbody>
          </table>

          {rows.length > 0 && (
            <>
              <h4 className="ai-cost-subhead">By call type</h4>
              <table className="ai-cost-table">
                <thead>
                  <tr>
                    <th>Call</th>
                    <th>Calls</th>
                    <th>In (cached)</th>
                    <th>Out</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(([schema, row]) => (
                    <tr key={schema}>
                      <th>
                        {label_for(schema)}
                        {row?.model ? <span className="caption"> · {String(row.model)}</span> : null}
                      </th>
                      <td className="mono">{tokens(row?.calls)}</td>
                      <td className="mono">
                        {tokens(row?.input_tokens)}
                        {num(row?.cached_input_tokens) > 0 ? ` (${tokens(row?.cached_input_tokens)})` : ""}
                      </td>
                      <td className="mono">{tokens(row?.output_tokens)}</td>
                      <td className="mono">{rates_missing ? "—" : usd(row?.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <p className="caption">
            An estimate: token counts come straight from OpenAI's usage report on each response,
            priced with the per-model rates in <code>openai.json</code>. It covers this person only,
            and is saved with their run, so it keeps counting across sessions and devices.
          </p>
        </Modal>
      )}
    </>
  );
}
